import { ExpenseTypeRecord } from './supabase';

export type SupportedLanguage = 'lv' | 'en' | 'de' | 'ru' | 'lt' | 'et' | 'es' | 'pt' | 'pl' | 'no' | 'sl' | 'sv' | 'ro' | 'nl' | 'it' | 'fr' | 'hr';

/**
 * Get the translated name for an expense type based on the user's language preference
 */
export function getExpenseTypeName(expenseType: Partial<ExpenseTypeRecord>, language: SupportedLanguage = 'en'): string {
    const languageKey = `name_${language}` as keyof ExpenseTypeRecord;
    const translatedName = expenseType[languageKey] as string | undefined;

    // Fallback chain: requested language -> English -> type_key
    return translatedName || expenseType.name_en || expenseType.type_key || '';
}

/**
 * Get the translated name from the new defaultExpenseTypes.json format
 * which uses a translations object instead of separate name_* fields
 */
export function getExpenseTypeNameFromData(expenseTypeData: any, language: SupportedLanguage = 'en'): string {
    if (!expenseTypeData) return '';

    // New format: { translations: { en: "...", lv: "...", ... } }
    if (expenseTypeData.translations) {
        return expenseTypeData.translations[language] || expenseTypeData.translations['en'] || expenseTypeData.type_key || '';
    }

    // Fallback to old format
    return getExpenseTypeName(expenseTypeData, language);
}

/**
 * Normalizes legacy or inconsistent expense type keys to the current keys in defaultExpenseTypes.json
 */
export function normalizeExpenseTypeKey(typeKey: string): string {
    if (!typeKey) return 'other';
    const key = typeKey.toLowerCase();
    if (key === 'car_wash') return 'wash';
    // Legacy keys → new service_* keys
    if (key === 'repairs') return 'service_repair';
    if (key === 'repair') return 'service_repair';
    if (key === 'maintenance') return 'service_maintenance';
    if (key === 'regular_maintenance') return 'service_maintenance';
    if (key === 'upgrade' || key === 'service_upgrade') return 'service_tuning';
    return typeKey;
}

// ─── Service Group ────────────────────────────────────────────────────────────

/** Virtual key used as the "Serviss" group entry in the type picker UI */
export const SERVICE_GROUP_KEY = '__service_group' as const;

/** The three real expense-type keys that belong to the Service group */
export const SERVICE_SUBTYPES = [
    'service_repair',
    'service_maintenance',
    'service_tuning',
] as const;

export type ServiceSubtype = typeof SERVICE_SUBTYPES[number];

/** Returns true if the given key is one of the three service sub-types */
export function isServiceType(key: string): boolean {
    const normalized = normalizeExpenseTypeKey(key);
    return (SERVICE_SUBTYPES as readonly string[]).includes(normalized);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Update an expense type with a new name for a specific language
 */
export function setExpenseTypeName(
    expenseType: Partial<ExpenseTypeRecord>,
    name: string,
    language: SupportedLanguage
): Partial<ExpenseTypeRecord> {
    const languageKey = `name_${language}` as keyof ExpenseTypeRecord;

    return {
        ...expenseType,
        [languageKey]: name,
    };
}

/**
 * When creating a new expense type, fill all language fields with the same value
 */
export function createExpenseTypeWithAllLanguages(name: string): Partial<ExpenseTypeRecord> {
    return {
        name_lv: name,
        name_en: name,
        name_ru: name,
        name_lt: name,
        name_et: name,
        name_es: name,
        name_pt: name,
        name_pl: name,
        name_no: name,
        name_sl: name,
        name_sv: name,
        name_ro: name,
        name_nl: name,
        name_it: name,
        name_fr: name,
        name_hr: name,
    };
}
